import React, { useEffect, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop'

import Button from '@components/shared/Button'
import Dialog from '@components/shared/Dialog'
import imagePreview from '@components/shared/ImageUploader/utils/imagePreview'
import { uploadCollectionImageData } from '@services/CollectionService'
import { useDialogStore } from '@stores/dialog.store'

import type { UploadCollectionImageResponse } from '@services/CollectionService'
import type { Crop, PixelCrop } from 'react-image-crop'

import 'react-image-crop/dist/ReactCrop.css'

interface Props {
  imgSrc: string
  aspect: number
  onUpload: (resp: UploadCollectionImageResponse) => Promise<boolean>
}

const ImageCropDialog: React.FC<Props> = ({ imgSrc, aspect, onUpload }) => {
  const { t } = useTranslation()
  const closeDialog = useDialogStore((s) => s.closeDialog)

  const imageRef = useRef<HTMLImageElement>(null)
  const [crop, setCrop] = useState<Crop>()
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>()
  const [uploading, setUploading] = useState(false)

  useEffect(() => {
    setCrop(undefined) // Makes crop preview update between images
  }, [imgSrc])

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    if (aspect) {
      const { width, height } = e.currentTarget
      setCrop(centerAspectCrop(width, height, aspect))
    }
  }

  return (
    <Dialog name="image_cropper" title={t('content.imageEditor')} maxWidth={800}>
      <p className="mb-2">{t('content.imageEditorDescription')}</p>
      <div className="text-center block relative mb-6 max-h-[400px]">
        {!!imgSrc && (
          <ReactCrop
            crop={crop}
            ruleOfThirds
            keepSelection
            onChange={(_, percentCrop) => setCrop(percentCrop)}
            onComplete={(c) => setCompletedCrop(c)}
            className="max-h-[400px]"
            aspect={aspect}>
            <img ref={imageRef} alt="Cropper" src={imgSrc} onLoad={onImageLoad} />
          </ReactCrop>
        )}
      </div>
      <div className="flex flex-row items-center">
        <div className="flex-1">
          <p className="max-w-[430px] text-gray-text dark:text-gray-text-dm">
            {t('content.imageEditorCopyright')}
          </p>
        </div>
        <Button variant="secondary" onClick={() => closeDialog('image_cropper')}>
          {t('action.cancel')}
        </Button>
        <Button
          variant="primary"
          className="ml-3"
          disabled={uploading}
          onClick={async () => {
            if (imageRef.current && completedCrop) {
              const url = imagePreview(imageRef.current, completedCrop)
              setUploading(true)
              const resp = await uploadCollectionImageData(url)
              if (resp) {
                const success = await onUpload(resp)
                if (success) closeDialog('image_cropper')
              }
              setUploading(false)
            }
          }}>
          {t(uploading ? 'messages.saving' : 'action.save')}
        </Button>
      </div>
    </Dialog>
  )
}

export default ImageCropDialog

function centerAspectCrop(mediaWidth: number, mediaHeight: number, aspect: number): Crop {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  )
}
